"use client"

import * as React from "react"

import {
  HoverCard,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import {
  QualityHoverCardContent,
  QuestionQualityCardBody,
  SETTING_QUALITY_HOVER_CONTENT_CLASS,
  SETTING_QUALITY_HOVER_WIDTH,
} from "@/components/question-quality/question-quality-mark"
import { leoInspectorSuggestionDotClass } from "@/components/question-quality/leo-inspector-suggestion-dot"
import { cn } from "@/lib/utils"
import {
  type QuestionQualityAlternate,
  type QuestionQualityCheck,
} from "@/lib/question-quality"

function markAriaLabel(check: QuestionQualityCheck) {
  const severity = check.severity === "review" ? "Review" : "Caution"
  return `${severity}: ${check.title}. Open suggestion.`
}

export function QuestionQualitySettingMark({
  check,
  label,
  children,
  siblingChecks,
  disabled,
  focused,
  className,
  onUse,
  onDismiss,
  onOpenPane,
}: {
  check: QuestionQualityCheck
  label: React.ReactNode
  children: React.ReactNode
  siblingChecks?: QuestionQualityCheck[]
  disabled?: boolean
  focused?: boolean
  className?: string
  onUse: (
    check: QuestionQualityCheck,
    secondary?: boolean,
    alternate?: QuestionQualityAlternate,
  ) => void
  onDismiss: (check: QuestionQualityCheck) => void
  onOpenPane: (check: QuestionQualityCheck) => void
}) {
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const [open, setOpen] = React.useState(false)
  const closeTimer = React.useRef<number>(0)

  React.useEffect(() => {
    if (!focused) return
    anchorRef.current?.scrollIntoView({ block: "nearest" })
  }, [focused, check.id])

  React.useEffect(() => {
    return () => window.clearTimeout(closeTimer.current)
  }, [])

  const show = React.useCallback(() => {
    window.clearTimeout(closeTimer.current)
    setOpen(true)
  }, [])

  const hide = React.useCallback(() => {
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpen(false), 160)
  }, [])

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={80} closeDelay={120}>
      <div
        ref={anchorRef}
        id={`qq-setting-${check.id}`}
        role="group"
        aria-label={markAriaLabel(check)}
        aria-current={focused ? "true" : undefined}
        className={cn(
          "flex flex-col gap-1.5",
          className,
          disabled && "opacity-60",
        )}
      >
        <div
          className="relative inline-flex w-fit min-w-0 items-center gap-0.5"
          onPointerEnter={show}
          onPointerLeave={hide}
        >
          {label}
          <span
            aria-hidden="true"
            className="inline-flex size-4 shrink-0 items-center justify-center"
          >
            <span
              className={cn(
                "block size-2 rounded-full",
                leoInspectorSuggestionDotClass("review"),
              )}
            />
          </span>
          <HoverCardTrigger asChild>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
            />
          </HoverCardTrigger>
        </div>
        {children}
      </div>
      <QualityHoverCardContent
        hoverWidth={SETTING_QUALITY_HOVER_WIDTH}
        className={SETTING_QUALITY_HOVER_CONTENT_CLASS}
        onPointerEnter={show}
        onPointerLeave={hide}
      >
        <QuestionQualityCardBody
          check={check}
          onUse={onUse}
          onDismiss={onDismiss}
          onOpenPane={onOpenPane}
          onClose={() => setOpen(false)}
          disabled={disabled}
          siblingChecks={siblingChecks}
          layout="popover"
          outlineApplyCta
          compact
        />
      </QualityHoverCardContent>
    </HoverCard>
  )
}
